import { symbolAsyncIterator } from './compatibility.js';
import { doneResult, valueResult } from './stream_transform.js';
/**
 * Throttle a function to be called at most once every "wait" milliseconds,
 * on the trailing edge.
 *
 * Roughly equivalent to lodash/throttle with {leading: false, trailing: true}
 */
export function throttleTrailing(func, wait) {
    let timeoutId = null;
    const later = () => {
        func();
        timeoutId = null;
    };
    return function () {
        if (timeoutId == null) {
            timeoutId = setTimeout(later, wait);
        }
    };
}
export function asyncNotifier() {
    const queue = new EventQueue();
    return {
        notify() {
            if (queue.countOutstandingEvents > 0) {
                // Already has an outstanding event, no need to buffer another one.
            }
            else {
                queue.notify();
            }
        },
        waitForNotification(signal) {
            return queue.waitForEvent(signal);
        }
    };
}
export class EventQueue {
    options;
    waitingConsumer;
    outstandingEvents;
    constructor(options = {}) {
        this.options = options;
        this.outstandingEvents = [];
    }
    /**
     * The amount of buffered events not yet dispatched to listeners.
     */
    get countOutstandingEvents() {
        return this.outstandingEvents.length;
    }
    notifyInner(dispatch) {
        const existing = this.waitingConsumer;
        this.waitingConsumer = undefined;
        const dispatchAndNotifyListeners = (waiter) => {
            dispatch(waiter);
            this.options.eventDelivered?.();
        };
        if (existing) {
            dispatchAndNotifyListeners(existing);
        }
        else {
            this.outstandingEvents.push(dispatchAndNotifyListeners);
        }
    }
    notify(value) {
        this.notifyInner((l) => l.resolve(value));
    }
    notifyError(error) {
        this.notifyInner((l) => l.reject(error));
    }
    waitForEvent(signal) {
        return new Promise((resolve, reject) => {
            if (this.waitingConsumer != null) {
                throw new Error('Illegal call to waitForEvent, already has a waiter.');
            }
            const complete = () => {
                signal?.removeEventListener('abort', onAbort);
            };
            const onAbort = () => {
                complete();
                this.waitingConsumer = undefined;
                resolve(undefined);
            };
            const waiter = {
                resolve: (value) => {
                    complete();
                    resolve(value);
                },
                reject: (error) => {
                    complete();
                    reject(error);
                }
            };
            if (signal.aborted) {
                resolve(undefined);
            }
            else if (this.countOutstandingEvents > 0) {
                const [event] = this.outstandingEvents.splice(0, 1);
                event(waiter);
            }
            else {
                this.waitingConsumer = waiter;
                signal.addEventListener('abort', onAbort);
            }
        });
    }
    /**
     * Creates an async iterable backed by event queues.
     *
     * @param run A function invoked for every new listener. It receives a queue backing the async iterator.
     * @param abort An additional abort signal. The `run` callback will also be aborted when `AsyncIterator.return` is
     * called.
     * @returns An object conforming to the async iterable protocol.
     */
    static queueBasedAsyncIterable(run, abort) {
        return {
            [symbolAsyncIterator]: () => {
                const queue = new EventQueue();
                const controller = new AbortController();
                function dispose() {
                    controller.abort();
                    abort?.removeEventListener('abort', dispose);
                }
                if (abort) {
                    if (abort.aborted) {
                        controller.abort();
                    }
                    else {
                        abort.addEventListener('abort', dispose);
                    }
                }
                run(queue, controller.signal);
                return {
                    async next() {
                        const event = await queue.waitForEvent(controller.signal);
                        return event == null ? doneResult : valueResult(event);
                    },
                    async return() {
                        dispose();
                        return doneResult;
                    }
                };
            }
        };
    }
}
//# sourceMappingURL=async.js.map